import axiosInstance from "../../utils/axiosInstance";

/**
 * THE WORKSPACE ADAPTER. One place that knows the wire.
 *
 * Every workspace read goes through here so the envelope is parsed once and
 * every failure reaches the slice in ONE shape:
 *
 *   { status, code, retryable, fieldErrors, retryAfter, message, aborted }
 *
 * The backend contract (`.wt-squad-workspace-b3` @ 865b2074):
 *
 *   success   `{ success: true, data: {...}, message }`, the payload is `data`.
 *   failure   `{ success: false, data: { code, retryable, request_id },
 *               message, errors: {...} }` from `HandlesWorkspaceErrors`. The
 *             code lives under `data.code`; 422 field errors are top level.
 *   429       NOT the workspace envelope (`app/Exceptions/Handler.php`
 *             answers `{ error, status_code, context }`), so it is keyed on
 *             the HTTP status and the `Retry-After` header, never on a code.
 *
 * The base URL already ends in `/api/v1` (`REACT_APP_API_URL`), so paths here
 * start at `/workspace`. Context travels as query parameters on every GET,
 * which is what `WorkspaceContextRequest` validates.
 *
 * Mutations answer OPERATION RECEIPTS (plan 4.5). They describe one
 * operation and are never written into the entity cache; the caller reconciles
 * with `getEntity`. Receipts are normalized here to `{ file_id, ... }`
 * because the two write routes answer different shapes: upload answers the
 * file DTO (`data.id`), revision answers `{ file_id, revision_id,
 * revision_number, lock_version }`.
 */

/** A request the caller cancelled. Never an error a learner should see. */
const isAbort = (error) =>
  error?.name === "CanceledError" ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "AbortError";

const parseRetryAfter = (headers) => {
  const raw = headers?.["retry-after"] ?? headers?.["Retry-After"];
  if (raw === undefined || raw === null || raw === "") return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const at = Date.parse(raw);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.round((at - Date.now()) / 1000));
};

/**
 * Every rejection, in one shape.
 *
 * A request with no response is a network failure, which is retryable and has
 * no code the server chose. A 429 carries the synthetic code `rate_limited`
 * because the server's throttle answer has none.
 */
export const normalizeError = (error) => {
  if (isAbort(error)) {
    return {
      status: 0,
      code: "aborted",
      retryable: false,
      fieldErrors: {},
      retryAfter: null,
      message: "",
      aborted: true,
    };
  }
  const response = error?.response;
  if (!response) {
    return {
      status: 0,
      code: "network_error",
      retryable: true,
      fieldErrors: {},
      retryAfter: null,
      message: "",
      aborted: false,
    };
  }
  const status = Number(response.status) || 0;
  const body = response.data ?? {};
  if (status === 429) {
    /* FE3 pre-commit review F-S2-1: a 429 in the WORKSPACE envelope names its
       cause (`provider_rate_limited` is Google refusing, not thinccc), so its
       code is kept. Only thinccc's own throttle answer, which carries no
       envelope, gets the synthetic `rate_limited`. */
    const throttled = body && typeof body === "object" ? body.data : null;
    return {
      status,
      code:
        throttled &&
        typeof throttled === "object" &&
        typeof throttled.code === "string"
          ? throttled.code
          : "rate_limited",
      retryable: true,
      fieldErrors: {},
      retryAfter: parseRetryAfter(response.headers),
      message: "",
      aborted: false,
    };
  }
  const envelope = body && typeof body === "object" ? body.data : null;
  const code =
    envelope &&
    typeof envelope === "object" &&
    typeof envelope.code === "string"
      ? envelope.code
      : null;
  return {
    status,
    code,
    retryable:
      envelope && typeof envelope.retryable === "boolean"
        ? envelope.retryable
        : status >= 500,
    fieldErrors:
      body && body.errors && typeof body.errors === "object" ? body.errors : {},
    retryAfter: parseRetryAfter(response.headers),
    message: typeof body?.message === "string" ? body.message : "",
    aborted: false,
  };
};

const payloadOf = (response) => response?.data?.data;

const contextParams = ({ squadId, capsuleId, cursor = null, limit = null }) => {
  const params = { squad_id: squadId, capsule_id: capsuleId };
  if (cursor !== null && cursor !== undefined) params.cursor = cursor;
  if (limit !== null && limit !== undefined) params.limit = limit;
  return params;
};

const get = async (path, params, signal) => {
  try {
    const response = await axiosInstance(path, "get", undefined, {
      params,
      ...(signal ? { signal } : {}),
    });
    return payloadOf(response);
  } catch (error) {
    throw normalizeError(error);
  }
};

/** `GET /workspace`, the bootstrap. */
export const getWorkspace = ({ squadId, capsuleId, signal }) =>
  get("/workspace", contextParams({ squadId, capsuleId }), signal);

/**
 * One cursor page of a context collection.
 *
 * Cursors are ids, not page numbers: every list is `id` descending with
 * `where id < cursor`, and `next_cursor` is the last id served on the page
 * (`WorkspaceQueryService`).
 */
const COLLECTION_PATHS = {
  tasks: "/workspace/tasks",
  files: "/workspace/files",
  references: "/workspace/references",
};

export const getPage = ({
  collection,
  squadId,
  capsuleId,
  cursor = null,
  limit = null,
  signal,
}) =>
  get(
    COLLECTION_PATHS[collection],
    contextParams({ squadId, capsuleId, cursor, limit }),
    signal,
  );

/**
 * THE AUTHORITATIVE SINGLE-ENTITY READ.
 *
 * There is no `GET /workspace/files/{id}`. The lists answer instead: asking
 * for `cursor = id + 1` and `limit = 1` returns the highest id strictly below
 * `id + 1`, which is the entity itself whenever it is still visible in this
 * context, on whatever page it would otherwise sit.
 *
 * `item` is null when the first row is a different entity or the list is
 * empty: the entity is no longer visible here and the caller removes it.
 */
export const getEntity = async ({
  collection,
  squadId,
  capsuleId,
  id,
  signal,
}) => {
  const target = Number(id);
  const page = await getPage({
    collection,
    squadId,
    capsuleId,
    cursor: target + 1,
    limit: 1,
    signal,
  });
  const first = Array.isArray(page?.items) ? page.items[0] : null;
  return { item: first && Number(first.id) === target ? first : null };
};

/**
 * THE ACTOR'S OWN TASK DETAIL, through the existing authorized quest read.
 *
 * `QuestAPIController@show` answers the full resource for the owner and staff,
 * a peer projection for a current squad member, and 404 otherwise. The caller
 * asks only for the actor's own task, so a peer's planning prose is never
 * requested, and the result is held in component state, never in Redux.
 */
export const getOwnTaskDetail = async ({ questId, signal }) => {
  try {
    const response = await axiosInstance(
      `/quests/${Number(questId)}`,
      "get",
      undefined,
      signal ? { signal } : {},
    );
    return payloadOf(response);
  } catch (error) {
    throw normalizeError(error);
  }
};

const send = async (path, method, data, config = {}) => {
  try {
    const response = await axiosInstance(path, method, data, config);
    return payloadOf(response);
  } catch (error) {
    throw normalizeError(error);
  }
};

/** `POST /workspace/files`, multipart. `questId` also links the new file. */
export const uploadFile = async ({
  squadId,
  capsuleId,
  file,
  questId = null,
  idempotencyKey,
  signal,
}) => {
  const form = new FormData();
  form.append("squad_id", String(squadId));
  form.append("capsule_id", String(capsuleId));
  form.append("file", file);
  if (questId) form.append("quest_id", String(questId));
  form.append("idempotency_key", idempotencyKey);
  const data = await send(
    "/workspace/files",
    "post",
    form,
    signal ? { signal } : {},
  );
  return { file_id: Number(data?.id) };
};

/** `POST /workspace/files/{id}/revisions`, multipart, with the lock version. */
export const uploadRevision = async ({
  fileId,
  squadId,
  capsuleId,
  file,
  expectedLockVersion,
  idempotencyKey,
  signal,
}) => {
  const form = new FormData();
  form.append("squad_id", String(squadId));
  form.append("capsule_id", String(capsuleId));
  form.append("file", file);
  form.append("expected_lock_version", String(expectedLockVersion));
  form.append("idempotency_key", idempotencyKey);
  const data = await send(
    `/workspace/files/${Number(fileId)}/revisions`,
    "post",
    form,
    signal ? { signal } : {},
  );
  return {
    file_id: Number(data?.file_id),
    revision_id: data?.revision_id ?? null,
    revision_number: data?.revision_number ?? null,
    lock_version: data?.lock_version ?? null,
  };
};

/** `PUT /workspace/tasks/{quest}/files/{file}`: context in the body. */
export const linkTaskFile = ({ questId, fileId, squadId, capsuleId }) =>
  send(`/workspace/tasks/${Number(questId)}/files/${Number(fileId)}`, "put", {
    squad_id: squadId,
    capsule_id: capsuleId,
  });

/**
 * `DELETE /workspace/tasks/{quest}/files/{file}`: context as QUERY
 * parameters. `axiosInstance` sends DELETE with a config and no body.
 */
export const unlinkTaskFile = ({ questId, fileId, squadId, capsuleId }) =>
  send(
    `/workspace/tasks/${Number(questId)}/files/${Number(fileId)}`,
    "delete",
    undefined,
    { params: { squad_id: squadId, capsule_id: capsuleId } },
  );

/**
 * `POST /workspace/files/{id}/access` for an UPLOADED file. `capsuleId` is
 * the FILE's own capsule (an earlier level for earlier work). Answers
 * `{ kind, url, expires_at, file_id, revision_id }`, a 300 s signed URL that
 * the caller keeps in component state only.
 */
export const getAccess = ({
  fileId,
  squadId,
  capsuleId,
  intent,
  revisionId = null,
  signal,
}) =>
  send(
    `/workspace/files/${Number(fileId)}/access`,
    "post",
    {
      squad_id: squadId,
      capsule_id: capsuleId,
      intent,
      ...(revisionId ? { revision_id: revisionId } : {}),
    },
    signal ? { signal } : {},
  );

/* ===================== FE3: CAPTURE, SUBMISSIONS, COPY =====================
   Every route read from the deployed backend (origin/main 421b2e6a,
   `routes/api.php:716,720-723` and `:702`). Capture bodies carry exactly the
   keys `WorkspaceCaptureRequest` allows: unknown fields are a 422, so an
   optional key is omitted rather than sent empty. */

const CAPTURE_PART_KEYS = ["file_id", "revision_id", "export_format"];

const capturePart = (part) => {
  const out = {};
  CAPTURE_PART_KEYS.forEach((key) => {
    const value = part?.[key];
    if (value === null || value === undefined || value === "") return;
    out[key] = key === "export_format" ? String(value) : Number(value);
  });
  return out;
};

/**
 * `POST /workspace/captures` → 202 `{ capture_id, status, expires_at }`.
 * `parts` is ordered; a video link rides as `video_link_url` and counts
 * toward the ten-part bound on the server.
 */
export const createCapture = ({
  squadId,
  capsuleId,
  expectedSubmissionVersion,
  idempotencyKey,
  parts,
  videoLinkUrl = null,
  signal,
}) =>
  send(
    "/workspace/captures",
    "post",
    {
      squad_id: Number(squadId),
      capsule_id: Number(capsuleId),
      expected_submission_version: expectedSubmissionVersion,
      idempotency_key: idempotencyKey,
      parts: (Array.isArray(parts) ? parts : []).map(capturePart),
      ...(videoLinkUrl ? { video_link_url: videoLinkUrl } : {}),
    },
    signal ? { signal } : {},
  );

/**
 * `GET /workspace/captures/{uuid}`, originator only and unthrottled. Every
 * read reissues each ready part's 5-minute preview descriptor.
 */
export const getCapture = async ({ captureId, signal }) => {
  try {
    const response = await axiosInstance(
      `/workspace/captures/${encodeURIComponent(String(captureId))}`,
      "get",
      undefined,
      signal ? { signal } : {},
    );
    return payloadOf(response);
  } catch (error) {
    throw normalizeError(error);
  }
};

/**
 * `POST /workspace/captures/{uuid}/commit` → the receipt
 * `{ capture_id, milestone_file_id, submission_ids, submission_version,
 * attempt }`. A committed capture answers its stored receipt on replay, so an
 * uncertain commit is retried with the SAME body.
 */
export const commitCapture = ({
  captureId,
  manifestHash,
  expectedSubmissionVersion,
}) =>
  send(
    `/workspace/captures/${encodeURIComponent(String(captureId))}/commit`,
    "post",
    {
      manifest_hash: manifestHash,
      expected_submission_version: expectedSubmissionVersion,
    },
  );

/** `GET /workspace/submissions`: one cursor page, id descending. */
export const listSubmissions = ({
  squadId,
  capsuleId,
  cursor = null,
  limit = null,
  signal,
}) =>
  get(
    "/workspace/submissions",
    contextParams({ squadId, capsuleId, cursor, limit }),
    signal,
  );

/**
 * `GET /deliverables/{id}/signed-url`. A file row answers a one-hour signed
 * URL; a LINK row answers its external URL, which is not a fixed copy. The
 * credential read lock answers 403 `act1_incomplete` with no URL.
 */
export const getSubmissionSignedUrl = async ({ submissionId, signal }) => {
  try {
    const response = await axiosInstance(
      `/deliverables/${Number(submissionId)}/signed-url`,
      "get",
      undefined,
      signal ? { signal } : {},
    );
    return payloadOf(response);
  } catch (error) {
    throw normalizeError(error);
  }
};

/**
 * `POST /workspace/files/{id}/copy`: materializes an earlier UPLOAD reference
 * as a file of the current level (`WorkingFileService::copy`). Answers the new
 * file DTO with 201; normalized to a receipt like `uploadFile`.
 */
export const copyFile = async ({
  fileId,
  squadId,
  targetCapsuleId,
  revisionId,
  idempotencyKey,
  signal,
}) => {
  const data = await send(
    `/workspace/files/${Number(fileId)}/copy`,
    "post",
    {
      squad_id: Number(squadId),
      target_capsule_id: Number(targetCapsuleId),
      revision_id: Number(revisionId),
      idempotency_key: idempotencyKey,
    },
    signal ? { signal } : {},
  );
  return { file_id: Number(data?.id), file: data ?? null };
};

/**
 * `POST /workspace/files/{id}/access` with `intent=capture`: the Google
 * preflight under the current actor. Answers `{ file_id,
 * connection_generation, can_download, checked_at }`, or 409
 * `provider_authorization_required` when the per-file Picker step is needed.
 */
export const preflightGoogleCapture = ({
  fileId,
  squadId,
  capsuleId,
  expectedGeneration,
  signal,
}) =>
  send(
    `/workspace/files/${Number(fileId)}/access`,
    "post",
    {
      squad_id: Number(squadId),
      capsule_id: Number(capsuleId),
      intent: "capture",
      expected_connection_generation: Number(expectedGeneration),
    },
    signal ? { signal } : {},
  );
